import { Divider } from "@mui/material";
import { useMemo } from "react";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { EditFormDialog } from "./EditFormDialog";
import ClinicInformation from "./NewReqClinicInformation";
import PatientInformationAddress from "./NewReqPatientInformationAddress";
import PatientInformationBasic from "./NewReqPatientInformationBasic";
import PatientInformationEthnicity from "./NewReqPatientInformationEthnicity";
import PatientInformationFamilyHistory from "./NewReqPatientInformationFamilyHistory";
import { MutedDisabledLabels } from "./NewRequisition.styles";
import { OrderDetailsMetadata } from "./OrderDetailsDialog";
import { patientSectionDividerSx } from "./testPanelStyles";
import { requestReportUpdate } from "./Services/NewReqService";
import {
  buildReportUpdatePayload,
  convertRequisitionToReportUpdateForm,
  hasReportUpdateValidationError,
} from "./reportUpdate";

const SUBMIT_ERROR_MESSAGE =
  "We couldn't submit your request. Please try again or contact customer service.";

export const RequestReportUpdateDialog: React.FC<{
  requisition?: DetailedRequisitionSerializer;
  onClose: () => void;
  onSubmitted: () => Promise<void> | void;
}> = ({ requisition, onClose, onSubmitted }) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const originalForm = useMemo(
    () =>
      requisition
        ? convertRequisitionToReportUpdateForm(requisition)
        : undefined,
    [requisition],
  );

  if (!auth || !requisition?.sfid || !originalForm) return null;
  const { sfid } = requisition;

  return (
    <EditFormDialog
      id="request-report-update-dialog"
      title={`REQUEST AN UPDATE FOR ORDER ${requisition.req_identifier}`}
      submitLabel="SUBMIT REQUEST"
      originalForm={originalForm}
      getChanges={(form) => buildReportUpdatePayload(form, originalForm)}
      getValidationError={(form) =>
        hasReportUpdateValidationError(form, originalForm)
      }
      onSubmitClicked={() =>
        TrackAnalyticsEvent(
          "ProviderReportUpdateSubmitClicked",
          auth.token,
          auth.username,
          {
            source: "report_update_dialog",
            sfid,
          },
        )
      }
      onSubmit={async (form) => {
        const result = await requestReportUpdate(
          sfid,
          buildReportUpdatePayload(form, originalForm),
          auth.token,
          setLoggedOut,
        );
        if (!result.ok) return result.error || SUBMIT_ERROR_MESSAGE;
        await onSubmitted();
      }}
      onClose={onClose}
    >
      <OrderDetailsMetadata
        orderNumber={requisition.req_identifier}
        created={requisition.electronic_order}
      />
      <MutedDisabledLabels>
        <ClinicInformation
          preflight={false}
          submittedRequisition={originalForm}
          isEditMode
        />
        <PatientInformationBasic isEditMode isReportUpdate />
        <Divider sx={patientSectionDividerSx} />
        <PatientInformationAddress disabled />
        <Divider sx={patientSectionDividerSx} />
        <PatientInformationEthnicity disabled />
        <PatientInformationFamilyHistory disabled />
      </MutedDisabledLabels>
    </EditFormDialog>
  );
};
